import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { DashboardWrapper, SentryErrorButton } from "./Styles";
import { Posts } from "../../Components/Tabs/Posts/Posts";
//@ts-ignore
import IntroCard from "../../Components/Dashboard/IntroCard/IntroCard";
import {
  useGetProfileQuery,
  useLazyGetAllPostsQuery,
  useConfirmCheckoutSessionMutation,
} from "../../../redux/reducers/apiSlice";
import { NUMBER_OF_LOGINS_BEFORE_HIDING_INTRO } from "../../utils/constants";
import SuggestedForYouMobile from "../../Components/SuggestedForYouMobile/SuggestedForYouMobile";
import useScreenType from "react-screentype-hook";
import { Button } from "../../Components/ui/button";
// import { AmbassadorBar } from "../../Components/AmbassadorBar/AmbassadorBar";
import { Menu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

const Dashboard = () => {
  const navigate = useNavigate();
  const [
    getAllPostsQuery,
    { data: pagePosts, isLoading: postsLoading, isError: isPostsError },
  ] = useLazyGetAllPostsQuery({ maxRetries: 0 });

  const { data: currentProfile, refetch: refetchProfile } = useGetProfileQuery();
  const [confirmCheckoutSession] = useConfirmCheckoutSessionMutation();
  const [page, setPage] = useState(1);

  // Handle Stripe upgrade redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const upgradeStatus = params.get("upgrade");
    const sessionId = params.get("session_id");

    const cleanUrl = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("upgrade");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.toString());
    };

    if (upgradeStatus === "success" && sessionId) {
      // Store in sessionStorage so TabSubscription can also confirm if needed
      sessionStorage.setItem("pending_session_id", sessionId);
      const run = async () => {
        try {
          await confirmCheckoutSession({ session_id: sessionId }).unwrap();
          sessionStorage.removeItem("pending_session_id");
          await refetchProfile();
          toast.success("🎉 You're now on the paid plan! Welcome to IGNITE Premium.");
        } catch (err: any) {
          console.error("ConfirmSessionError", err);
          // Try once more after a short delay (Stripe may need extra time)
          try {
            await new Promise((r) => setTimeout(r, 2000));
            await confirmCheckoutSession({ session_id: sessionId }).unwrap();
            sessionStorage.removeItem("pending_session_id");
            await refetchProfile();
            toast.success("🎉 You're now on the paid plan! Welcome to IGNITE Premium.");
          } catch (retryErr: any) {
            console.error("ConfirmSessionRetryError", retryErr);
            toast.error(
              "Payment was received by Stripe but we could not confirm your upgrade automatically. Open Settings → Subscription and click 'Already paid? Click here to refresh your status'."
            );
          }
        }
        cleanUrl();
      };
      run();
    } else if (upgradeStatus === "cancelled") {
      toast("Upgrade cancelled. You can try again anytime from Settings.", { icon: "ℹ️" });
      cleanUrl();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [allPosts, setAllPosts] = useState(pagePosts || []);
  /** Same breakpoints as Header/Sidebar/Posts (mobile = under 768px) */
  const { isMobile } = useScreenType();

  const [keysPressed, setKeysPressed] = useState({
    E: false,
    R: false,
    O: false,
  });
  const [showSentryErrorButton, setShowSentryErrorButton] = useState(false);
  const postsErrorToastShown = useRef(false);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const { key } = event;

      if (key === "e" || key === "E") {
        setKeysPressed((prev) => ({ ...prev, E: true }));
      } else if (key === "r" || key === "R") {
        setKeysPressed((prev) => ({ ...prev, R: true }));
      } else if (key === "o" || key === "O") {
        setKeysPressed((prev) => ({ ...prev, O: true }));
      }

      // Check if all keys are pressed
      if (keysPressed.E && keysPressed.R && keysPressed.O) {
        setShowSentryErrorButton(true);
      }
    };

    const handleKeyUp = (event) => {
      const { key } = event;

      if (key === "e" || key === "E") {
        setKeysPressed((prev) => ({ ...prev, E: false }));
      } else if (key === "r" || key === "R") {
        setKeysPressed((prev) => ({ ...prev, R: false }));
      } else if (key === "o" || key === "O") {
        setKeysPressed((prev) => ({ ...prev, O: false }));
      }

      // Hide the button if any key is released
      setShowSentryErrorButton(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [keysPressed]);

  useEffect(() => {
    getAllPostsQuery({ limit: 5, page });
  }, [page]);

  useEffect(() => {
    if (pagePosts?.length && pagePosts?.length > 0) {
      if (page === 1) {
        setAllPosts(pagePosts);
      } else {
        setAllPosts([...allPosts, ...pagePosts]);
      }
    }
  }, [pagePosts]);

  useEffect(() => {
    if (isPostsError && !postsErrorToastShown.current) {
      postsErrorToastShown.current = true;
      toast.error(
        "Something went wrong while fetching the posts. Try again later"
      );
    }
    if (!isPostsError) {
      postsErrorToastShown.current = false;
    }
  }, [isPostsError]);

  const onResetPosts = () => {
    setPage(1);
  };

  const hasIntroCard = () => {
    const loginFlag = localStorage.getItem(
      `${currentProfile?.email}-loginFlag`
    );
    if (loginFlag) {
      return parseInt(loginFlag) <= NUMBER_OF_LOGINS_BEFORE_HIDING_INTRO;
    }
    return false;
  };

  // Get organization name from user's organizations
  const organizationName = currentProfile?.organizations?.[0]?.name || "Friend";
  const profileId = currentProfile?.id;
  const myOrganizations = currentProfile?.organizations || [];

  return (
    <DashboardWrapper $hasIntroCard={hasIntroCard()}>
      {showSentryErrorButton && (
        // @ts-ignore
        <SentryErrorButton onClick={() => methodDoNotExists()}>
          Create sentry error
        </SentryErrorButton>
      )}
      {hasIntroCard() && <IntroCard />}

      {/* <AmbassadorBar /> */}

      {/* Welcome Banner with Action Buttons */}
      <div className="mb-6 px-4 py-6 sm:px-6 sm:py-7 md:p-8 bg-gradient-to-r from-orange-500 to-pink-500 rounded-lg text-white shadow-lg">
        <h2 className="text-xl sm:text-2xl font-semibold mb-2">
          Welcome to Ignite!
        </h2>
        <p className="text-white/90 mb-4 sm:mb-6 text-sm sm:text-base">
          You're all set. Here's what you can do next:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <Link to="/reports/generate-ai" className="w-full">
            <Button 
              className="w-full bg-white text-orange-600 hover:bg-gray-50 font-semibold h-12 text-base shadow-md transition-all hover:shadow-lg"
            >
              Generate Report
            </Button>
          </Link>
          {profileId && (
            <Link to={`/users/${profileId}?editProfile=true`} className="w-full">
              <Button 
                variant="outline"
                className="w-full bg-transparent text-white border-2 border-white hover:bg-white/10 font-semibold h-12 text-base transition-all hover:border-white/80"
              >
                Complete Profile
              </Button>
            </Link>
          )}
          {myOrganizations.length > 0 && (
            <Menu>
              <MenuButton as="div" className="w-full" style={{ cursor: "pointer" }}>
                <Button
                  variant="outline"
                  className="w-full bg-transparent text-white border-2 border-white hover:bg-white/10 font-semibold h-12 text-base transition-all hover:border-white/80 cursor-pointer"
                  style={{ cursor: "pointer" }}
                >
                  Post to My Organization
                </Button>
              </MenuButton>
              <MenuList
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                boxShadow="lg"
                color="gray.800"
              >
                {myOrganizations.map((org) => (
                  <MenuItem
                    key={org.id}
                    onClick={() => navigate(`/organizations/${org.id}/profile`)}
                    color="gray.800"
                    bg="white"
                    _hover={{ bg: "gray.100", color: "gray.900" }}
                    _focus={{ bg: "gray.100", color: "gray.900" }}
                    style={{ cursor: "pointer" }}
                  >
                    {org.name}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          )}
        </div>
      </div>
      {isMobile && (
        <div
          style={{
            marginTop: hasIntroCard() ? "32px" : "24px",
            paddingTop: "8px",
            paddingBottom: "8px",
          }}
        >
          <SuggestedForYouMobile />
        </div>
      )}
      <Posts
        hasIntroCard={hasIntroCard()}
        showPostCreationForm={true}
        posts={allPosts}
        postsLoading={postsLoading}
        disableLoadMore={postsLoading}
        onLoadMore={() => {
          let currentPage = page;
          currentPage++;
          setPage(currentPage);
        }}
        resetPage={onResetPosts}
      />
    </DashboardWrapper>
  );
};

export default Dashboard;
